import React from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

import RSPopover from "reactstrap/lib/Popover";
import UncontrolledPopover from "reactstrap/lib/UncontrolledPopover";
import PopoverHeader from "reactstrap/lib/PopoverHeader";
import PopoverBody from "reactstrap/lib/PopoverBody";

import { gatherClasses, isFunction, requiredIf } from "../../lib/utils";
import "./popover.scss";

function isInDOMSubtree(element, subtreeRoot) {
  return (
    subtreeRoot && (element === subtreeRoot || subtreeRoot.contains(element))
  );
}

class Popover extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      target: null
    };
    this.ref = React.createRef();
    this.innerRef = React.createRef();
  }

  componentDidMount() {
    this.bindTarget();
    document.addEventListener("click", this.handleDocumentClick);
  }

  componentWillUnmount() {
    document.removeEventListener("click", this.handleDocumentClick);
  }

  handleDocumentClick = e => {
    const { hideOnOutsideClick, toggle } = this.props;
    if (
      hideOnOutsideClick &&
      isFunction(toggle) &&
      this.innerRef.current &&
      !isInDOMSubtree(e.target, this.innerRef.current)
    ) {
      toggle(e);
    }
  };

  bindTarget() {
    // eslint-disable-next-line react/no-find-dom-node
    const target = ReactDOM.findDOMNode(this.ref.current);
    if (target instanceof HTMLElement || target instanceof SVGElement) {
      this.setState({
        target
      });
    } else {
      // eslint-disable-next-line no-console
      console.error("Could not find target for the popover component.");
    }
  }

  renderPopoverContent = () => {
    const { title, body, bodyClassName } = this.props;
    return (
      // eslint-disable-next-line react/jsx-fragments
      <React.Fragment>
        {title && <PopoverHeader>{title}</PopoverHeader>}
        {body && (
          <PopoverBody className={bodyClassName}>
            {isFunction(body) ? body() : body}
          </PopoverBody>
        )}
      </React.Fragment>
    );
  };

  renderPopover = () => {
    const {
      size,
      disabled,
      placement,
      hideArrow,
      type,
      isOpen,
      toggle,
      trigger
    } = this.props;
    const { target } = this.state;
    const commonProps = {
      className: gatherClasses(this.props, `oui-popover-${size}`),
      delay: 0,
      disabled,
      placement,
      placementPrefix: "oui-bs-popover",
      target,
      hideArrow
    };

    if (target) {
      if (type === "controlled") {
        return (
          <RSPopover
            {...commonProps}
            innerRef={this.innerRef}
            boundariesElement="window"
            isOpen={isOpen}
            toggle={toggle}
            trigger={trigger}
          >
            {this.renderPopoverContent()}
          </RSPopover>
        );
      }
      return (
        <UncontrolledPopover
          {...commonProps}
          boundariesElement="window"
          trigger={trigger === "click" ? "legacy" : trigger}
        >
          {this.renderPopoverContent()}
        </UncontrolledPopover>
      );
    }

    return null;
  };

  render() {
    const { children } = this.props;
    return (
      // eslint-disable-next-line react/jsx-fragments
      <React.Fragment>
        {this.renderPopover()}
        <PopoverWrapper ref={this.ref}>{children}</PopoverWrapper>
      </React.Fragment>
    );
  }
}

Popover.propTypes = {
  title: PropTypes.string,
  body: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.element
  ]).isRequired,
  type: PropTypes.oneOf(["controlled", "uncontrolled"]),
  isOpen: requiredIf(PropTypes.bool, props => props.type === "controlled"),
  toggle: requiredIf(PropTypes.func, props => props.type === "controlled"),
  placement: PropTypes.oneOf([
    "auto",
    "auto-start",
    "auto-end",
    "top",
    "top-start",
    "top-end",
    "right",
    "right-start",
    "right-end",
    "bottom",
    "bottom-start",
    "bottom-end",
    "left",
    "left-start",
    "left-end"
  ]),
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(["xs", "sm", "md", "lg", "auto"]),
  bodyClassName: PropTypes.string,
  className: PropTypes.string,
  hideArrow: PropTypes.bool,
  hideOnOutsideClick: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]),
  trigger: PropTypes.string
};

Popover.defaultProps = {
  placement: "auto",
  disabled: false,
  type: "controlled",
  size: "sm",
  hideArrow: false,
  hideOnOutsideClick: false,
  trigger: "click"
};

Popover.displayName = "OrionPopover";

export default Popover;

/**
 * PopoverWrapper will help binding ref on the Popover children component
 * As that ref is further used to pass the target element to Popover component
 */
// eslint-disable-next-line react/prefer-stateless-function
class PopoverWrapper extends React.Component {
  render() {
    const { children } = this.props;
    // eslint-disable-next-line react/jsx-fragments
    return <React.Fragment>{children}</React.Fragment>;
  }
}

PopoverWrapper.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ])
};
